// apps/web/src/features/cargo/forms/useCargoPaymentDocUpload.ts
//
// Validate → presigned-PUT upload flow for Cargo payment documents (#1232).
// Mirrors the lead-file pattern: validate the SOURCE file (MIME/size/name)
// BEFORE any network call, presign (`scope='cargo-payment-doc'`) → PUT → return
// the issued objectKey. The caller THEN binds it via
// `POST /cargo/:id/payment-documents` (the bind step is the caller's, so the
// upload→bind ordering is observable). Never POST raw files directly.

import { LEAD_FILE_ALLOWED_MIME, type LeadFileMime } from '@magimp/shared';

import { requestUploadUrl } from '../../../api/requests/index.js';

// 25 MiB ceiling — mirrors the backend `cargo-payment-doc` scope limit.
export const CARGO_PAYMENT_DOC_MAX_BYTES = 25 * 1024 * 1024;
export const CARGO_PAYMENT_DOC_NAME_MAX = 256;
export const CARGO_PAYMENT_DOC_ACCEPT = LEAD_FILE_ALLOWED_MIME.join(',');

export type CargoPaymentDocError = 'wrongType' | 'tooLarge' | 'nameTooLong';

export interface CargoPaymentDocValidation {
  ok: boolean;
  error?: CargoPaymentDocError;
}

function isAllowedMime(type: string): type is LeadFileMime {
  return (LEAD_FILE_ALLOWED_MIME as readonly string[]).includes(type);
}

/** Pre-upload validation: MIME allowlist, size, and filename length. */
export function validateCargoPaymentDoc(file: File): CargoPaymentDocValidation {
  if (!isAllowedMime(file.type)) return { ok: false, error: 'wrongType' };
  if (file.size > CARGO_PAYMENT_DOC_MAX_BYTES) return { ok: false, error: 'tooLarge' };
  if (file.name.length > CARGO_PAYMENT_DOC_NAME_MAX) return { ok: false, error: 'nameTooLong' };
  return { ok: true };
}

export interface UploadedPaymentDoc {
  objectKey: string;
  fileName: string;
  contentType: string;
  contentLength: number;
}

/**
 * Presign + PUT a single validated payment document; resolves to the binding
 * payload the caller posts to `POST /cargo/:id/payment-documents`. Throws on a
 * presign/PUT failure so the caller surfaces it before the bind call.
 */
export async function uploadCargoPaymentDoc(file: File): Promise<UploadedPaymentDoc> {
  const signed = await requestUploadUrl({
    contentType: file.type,
    contentLength: file.size,
    scope: 'cargo-payment-doc',
  });
  const put = await fetch(signed.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'content-type': file.type },
  });
  if (!put.ok) throw new Error(`upload failed: ${put.status}`);
  return {
    objectKey: signed.objectKey,
    fileName: file.name,
    contentType: file.type,
    contentLength: file.size,
  };
}
